import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { Form } from '@unform/web';

import { Box, Divider, Grid, IconButton, Link, List, ListItem, ListItemIcon, ListItemText, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import LockIcon from '@material-ui/icons/Lock';

import imgBannerH from '../../assets/img/banner-h.png';
import imgLogo from '../../assets/img/logo.png';
import Button from '../../components/Form/Button';
import Input from '../../components/Form/Input';
import Loading from '../../components/Loading';
import Entidade from '../../constants/Entidade';
import TextHelper from '../../helpers/TextHelper';
import UtilHelper from '../../helpers/UtilHelper';
import ValidateHelper from '../../helpers/ValidateHelper';
import useAuth from '../../hooks/useAuth';
import useModal from '../../hooks/useModal';
import AuthService from '../../services/AuthService';
import useStyles from './useStyles';

export default function Login() {
  const classes = useStyles();

  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const modal = useModal();

  const { login, loginDireto, logout, setToken, user, setUser, context, setContext } = useAuth();

  const [loading, setLoading] = useState(false);
  const [recuperar, setRecuperar] = useState(null);
  const [direto, setDireto] = useState(false);
  const [contexts, setContexts] = useState(null);
  const [error, setError] = useState('');

  const query = new URLSearchParams(location.search);
  const redirect = query.get('redirect') || '/';

  const showError = useCallback(
    async (err, onConfirm = modal.close) => {
      await logout();

      setLoading(false);

      modal.alert({
        title: 'Oops!',
        content: UtilHelper.getReponseMessage(err),
        onConfirm,
      });
    },
    [logout, modal, setLoading],
  );

  const getContexts = useCallback(async () => {
    const { id_entidade } = params;
    const { data: contexts } = await AuthService.getContexts(id_entidade);

    if (!contexts?.length) {
      throw new Error('Você não possui acesso ao portal do egresso.');
    }

    setContexts(contexts);
  }, [params, setContexts]);

  const selectContext = async (context) => {
    setLoading(true);

    const { matricula, id_entidade } = context;

    try {
      modal.close();

      const { data } = await AuthService.atualizarEntidade(id_entidade);
      const { token } = data;

      setToken(token);
    } catch (err) {
      await showError(err);
    }

    setUser((user) => ({ ...user, ...matricula }));
    setContext(context);
    setLoading(false);

    // Redirectionar para home
    history.replace({ pathname: redirect });
  };

  // Verificar login direto
  useEffect(() => {
    const { id_entidade, id_usuario, st_urlacesso } = params;

    if (direto || !st_urlacesso) {
      return;
    }

    setDireto(true);

    (async () => {
      try {
        // Se a sessão atual for a mesma da solicitada por parâmetro, apenas redirecionar
        if (+user?.id_entidade === +id_entidade && +user?.id_usuario === +id_usuario && context) {
          history.replace({ pathname: redirect });
          return;
        }

        await logout();
        await loginDireto(id_entidade, id_usuario, st_urlacesso);
        await getContexts();
      } catch (err) {
        await showError(err, () => {
          history.replace({ pathname: '/login' });
          setDireto(false);
          modal.close();
        });
      }
    })();
  }, [direto, loginDireto, logout, modal, history, params, redirect, user, context, getContexts, showError]);

  useEffect(() => {
    if (!contexts) {
      return;
    }

    if (contexts?.length === 1) {
      selectContext(contexts[0]).catch(() => null);
      return;
    }

    setLoading(false);

    modal.alert({
      title: 'Selecione o contexto',
      confirmButton: 'Cancelar',
      onConfirm: async () => {
        modal.close();
        await logout();
      },
      content: (
        <div style={{ margin: '0 -22px' }}>
          <List component="div">
            <Divider />
            {contexts.map((context) => (
              <React.Fragment key={context.id_entidade}>
                <ListItem
                  button
                  component="button"
                  onClick={() => selectContext(context)}
                  id={`${TextHelper.replaceSpecialChars(context.st_contexto).toLowerCase()}-${context.id_entidade}`}
                >
                  <ListItemIcon style={{ minWidth: 40 }}>
                    <KeyboardArrowRightIcon />
                  </ListItemIcon>
                  <ListItemText primary={context.st_contexto} />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </div>
      ),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contexts]);

  const handleEsqueciSenha = useCallback((e, newRecuperar) => {
    e.preventDefault();
    setError('');
    setRecuperar(newRecuperar);
  }, []);

  const handleSubmitLogin = async ({ username, password }) => {
    try {
      // Resetar erros
      setError('');

      if (!ValidateHelper.length(username, 3)) {
        setError('Informe o e-mail corretamente');
        return;
      }

      if (!ValidateHelper.length(password, 6)) {
        setError('Informe a senha corretamente');
        return;
      }

      setLoading(true);
      await login(username, password);
      await getContexts();
    } catch (err) {
      await showError(err);
    }
  };

  const handleSubmitRecuperar = async (data) => {
    data.id_entidade = Object.values(Entidade);

    const isEmailEnviado = Boolean(recuperar?.st_email);
    const isCodigoValidado = Boolean(recuperar?.st_hash);

    // Resetar erros
    setError('');

    let response;

    if (!ValidateHelper.email(data.st_email)) {
      return setError('Informe o e-mail corretamente');
    }

    if (isCodigoValidado) {
      if (!ValidateHelper.length(data.st_novasenha, 6, 50)) {
        return setError('A senha deve ter no mínimo 6 e no máximo 50 caracteres.');
      }

      if (data.st_novasenha !== data.st_confirmacaonovasenha) {
        return setError('As senhas não estão iguais.');
      }
    } else if (isEmailEnviado) {
      if (!ValidateHelper.length(data.st_hash, 8, 8)) {
        return setError('Informe o código.');
      }
    }

    setLoading(true);

    try {
      if (isCodigoValidado) {
        // Etapa de salvar NOVA SENHA
        response = await AuthService.salvarNovaSenhaComHash(data);
        setRecuperar(null);
      } else if (isEmailEnviado) {
        // Etapa de enviar CÓDIGO
        response = await AuthService.validarHashRecuperarSenha(data);
        setRecuperar((current) => ({ ...current, ...data }));
      } else {
        // Etapa de enviar E-MAIL
        response = await AuthService.enviarEmailHashRecuperarSenha(data);
        setRecuperar((current) => ({ ...current, ...data }));
      }

      modal.alert({ content: UtilHelper.getReponseMessage(response) });
    } catch (err) {
      showError(err);
    }

    setLoading(false);
  };

  if (direto) {
    return <Loading />;
  }

  return (
    <Box component={Grid} container className={classes.root}>
      <Grid item className={classes.colLeft} xs={12} sm={7} lg={8}>
        <div className={classes.welcome}>
          <h1>portal do egresso</h1>
          <h2>um ambiente exclusivo para você!</h2>
          <p>
            Aqui você encontrará um espaço para estreitar o seu relacionamento com a Unyleya e terá acesso a conteúdos relevantes, oportunidades e dicas sobre o
            mercado de trabalho, cursos gratuitos, descontos, promoções e muito mais!
          </p>
          <img className="responsive" src={imgBannerH} alt="" />
        </div>
      </Grid>
      <Grid item className={classes.colRight} xs={12} sm={5} lg={4}>
        <Form className={classes.form} onSubmit={recuperar ? handleSubmitRecuperar : handleSubmitLogin}>
          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={11} md={9}>
              <Box align="center" my={2}>
                <img className={classes.imgLogo} src={imgLogo} alt="Unyleya" />
              </Box>
              <Box my={2}>
                <Grid container alignItems="center">
                  {recuperar && (
                    <Grid item>
                      <IconButton title="Voltar" onClick={(e) => handleEsqueciSenha(e, null)} edge="start">
                        <ArrowBackIcon fontSize="small" color="secondary" />
                      </IconButton>
                    </Grid>
                  )}
                  <Grid item xs>
                    <Typography className={classes.formTitle} color="secondary">
                      {recuperar ? 'RECUPERAR SENHA' : 'LOGIN'}
                    </Typography>
                    {!recuperar && <Box fontSize={10}>O seu login e senha são os mesmos do Portal do Aluno.</Box>}
                  </Grid>
                </Grid>
              </Box>
              {Boolean(recuperar) && !Boolean(recuperar?.st_email) && (
                <Box my={2}>
                  <Typography>Por motivos de segurança, enviaremos um código de acesso para o seu e-mail.</Typography>
                </Box>
              )}
            </Grid>
            {!recuperar && (
              <>
                <Grid item container xs={11} md={9}>
                  <Input name="username" label="Usuário ou e-mail" color="secondary" autoFocus maxLength={255} />
                </Grid>
                <Grid item container xs={11} md={9}>
                  <Input type="password" name="password" label="Senha" color="secondary" maxLength={50} />
                </Grid>
                <Grid item xs={11} md={9}>
                  <Box display="flex" alignItems="center" justifyContent="flex-end">
                    <LockIcon fontSize="small" color="secondary" />
                    &nbsp;
                    <Typography component={Link} variant="body2" color="secondary" href="/#" onClick={(e) => handleEsqueciSenha(e, {})}>
                      Esqueceu a senha?
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}
            {recuperar && (
              <>
                <Grid item xs={11} md={9}>
                  <Input type="email" name="st_email" label="E-mail" color="secondary" autoFocus disabled={Boolean(recuperar?.st_email)} />
                </Grid>
                {Boolean(recuperar?.st_email) && !recuperar?.st_hash && (
                  <Grid item xs={11} md={9}>
                    <Input name="st_hash" label="Código" color="secondary" placeholder="Digite o código recebido" />
                  </Grid>
                )}
                {Boolean(recuperar?.st_hash) && (
                  <>
                    <Grid item xs={11} md={9}>
                      <Input name="st_novasenha" label="Nova Senha" color="secondary" placeholder="Digite a nova senha" />
                    </Grid>
                    <Grid item xs={11} md={9}>
                      <Input name="st_confirmacaonovasenha" label="Confirmar Nova Senha" color="secondary" placeholder="Repita a nova senha" />
                    </Grid>
                  </>
                )}
              </>
            )}
            <Grid item xs={11} md={9}>
              <Box color="error.main">
                <Typography variant="body2">{error || <span>&nbsp;</span>}</Typography>
              </Box>
            </Grid>
            <Grid item xs={11} md={9}>
              <Box mb={2} align="center">
                <Button type="submit" loading={loading} fullWidth color="secondary">
                  {recuperar ? (recuperar?.st_email ? 'CONTINUAR' : 'ENVIAR CÓDIGO') : 'ENTRAR'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Form>
      </Grid>
    </Box>
  );
}
